import type { CSSProperties } from 'react';

/**
 * Renderer warstwy z kolorem + szrafurą geologiczną.
 * Wzór budowany przez warstwowanie kilku CSS gradientów (`background-image`).
 *
 * Wspierane wzory:
 *   SOLID, DOTS, HORIZONTAL_LINES, VERTICAL_LINES,
 *   DIAGONAL_LINES, DIAGONAL_LINES_REVERSE, CROSS, BRICKS, TRIANGLES, WAVES.
 */

export const PATTERNS: Array<{ value: string; label: string }> = [
  { value: 'SOLID',                  label: 'Bez szrafury' },
  { value: 'DOTS',                   label: 'Kropki (piasek)' },
  { value: 'HORIZONTAL_LINES',       label: 'Poziome linie (glina)' },
  { value: 'VERTICAL_LINES',         label: 'Pionowe linie (piaskowiec)' },
  { value: 'DIAGONAL_LINES',         label: 'Ukośne linie (ił)' },
  { value: 'DIAGONAL_LINES_REVERSE', label: 'Ukośne odwrotne (łupek)' },
  { value: 'CROSS',                  label: 'Krzyże (gleba/torf)' },
  { value: 'BRICKS',                 label: 'Cegły (wapień)' },
  { value: 'TRIANGLES',              label: 'Trójkąty (anhydryt/gips)' },
  { value: 'WAVES',                  label: 'Fale (łupek)' },
  { value: 'CIRCLES',                label: 'Okręgi (żwir/pospółka)' },
  { value: 'PEBBLES',                label: 'Otoczaki / głazy' },
  { value: 'DASHES',                 label: 'Myślniki (mułek/pył)' },
  { value: 'BRICKS_DIAGONAL',        label: 'Cegły ukośne (dolomit)' },
  { value: 'BRICKS_MARL',            label: 'Cegły z kreską (margiel)' },
];

const STROKE = 'rgba(0,0,0,0.45)';

export function patternStyle(pattern: string | null | undefined, color: string | null | undefined): CSSProperties {
  const bg = color || '#94a3b8';
  switch (pattern) {
    case 'DOTS':
      return {
        backgroundColor: bg,
        backgroundImage: `radial-gradient(circle, ${STROKE} 1.2px, transparent 1.2px)`,
        backgroundSize: '6px 6px',
      };
    case 'HORIZONTAL_LINES':
      return {
        backgroundColor: bg,
        backgroundImage: `repeating-linear-gradient(0deg, transparent 0 4px, ${STROKE} 4px 5px)`,
      };
    case 'VERTICAL_LINES':
      return {
        backgroundColor: bg,
        backgroundImage: `repeating-linear-gradient(90deg, transparent 0 4px, ${STROKE} 4px 5px)`,
      };
    case 'DIAGONAL_LINES':
      return {
        backgroundColor: bg,
        backgroundImage: `repeating-linear-gradient(45deg, transparent 0 5px, ${STROKE} 5px 6px)`,
      };
    case 'DIAGONAL_LINES_REVERSE':
      return {
        backgroundColor: bg,
        backgroundImage: `repeating-linear-gradient(-45deg, transparent 0 5px, ${STROKE} 5px 6px)`,
      };
    case 'CROSS':
      return {
        backgroundColor: bg,
        backgroundImage:
          `repeating-linear-gradient(45deg, transparent 0 5px, ${STROKE} 5px 6px),` +
          `repeating-linear-gradient(-45deg, transparent 0 5px, ${STROKE} 5px 6px)`,
      };
    case 'BRICKS':
      return {
        backgroundColor: bg,
        backgroundImage:
          // pozioma + pionowe segmenty co drugą linię (prosty motyw cegieł)
          `repeating-linear-gradient(0deg, transparent 0 7px, ${STROKE} 7px 8px),` +
          `linear-gradient(90deg, ${STROKE} 1px, transparent 1px),` +
          `linear-gradient(90deg, transparent 0 50%, ${STROKE} 50%, ${STROKE} calc(50% + 1px), transparent calc(50% + 1px))`,
        backgroundSize: 'auto, 16px 16px, 16px 16px',
        backgroundPosition: '0 0, 0 0, 0 8px',
      };
    case 'TRIANGLES':
      return {
        backgroundColor: bg,
        backgroundImage:
          `linear-gradient(45deg, ${STROKE} 25%, transparent 25.5%, transparent 75%, ${STROKE} 75.5%),` +
          `linear-gradient(-45deg, ${STROKE} 25%, transparent 25.5%, transparent 75%, ${STROKE} 75.5%)`,
        backgroundSize: '10px 10px',
      };
    case 'WAVES':
      return {
        backgroundColor: bg,
        // dwa półokręgi tworzą fale
        backgroundImage:
          `radial-gradient(circle at 50% 100%, transparent 6px, ${STROKE} 6px, ${STROKE} 7px, transparent 7px)`,
        backgroundSize: '14px 8px',
      };
    case 'CIRCLES':
      return {
        backgroundColor: bg,
        backgroundImage: `radial-gradient(circle at 50% 50%, transparent 2px, ${STROKE} 2px, ${STROKE} 2.7px, transparent 2.7px)`,
        backgroundSize: '9px 9px',
      };
    case 'PEBBLES':
      return {
        backgroundColor: bg,
        backgroundImage:
          `radial-gradient(circle at 50% 50%, transparent 3px, ${STROKE} 3px, ${STROKE} 3.7px, transparent 3.7px)`,
        backgroundSize: '14px 12px',
      };
    case 'DASHES':
      return {
        backgroundColor: bg,
        backgroundImage:
          `repeating-linear-gradient(0deg, transparent 0 4px, ${STROKE} 4px 5px)`,
        backgroundSize: '7px 5px',
      };
    case 'BRICKS_DIAGONAL':
      return {
        backgroundColor: bg,
        backgroundImage:
          `repeating-linear-gradient(0deg, transparent 0 7px, ${STROKE} 7px 8px),` +
          `linear-gradient(90deg, ${STROKE} 1px, transparent 1px),` +
          `repeating-linear-gradient(45deg, transparent 0 3px, ${STROKE} 3px 3.6px)`,
        backgroundSize: 'auto, 16px 16px, auto',
      };
    case 'BRICKS_MARL':
      return {
        backgroundColor: bg,
        backgroundImage:
          `repeating-linear-gradient(0deg, transparent 0 7px, ${STROKE} 7px 8px),` +
          `linear-gradient(90deg, ${STROKE} 1px, transparent 1px)`,
        backgroundSize: 'auto, 12px 8px',
      };
    case 'SOLID':
    default:
      return { backgroundColor: bg };
  }
}

/** Mały kafelek do dropdownu/podglądu — 24×16 px ze szrafurą. */
export function HatchingSwatch({ pattern, color, className = '' }: {
  pattern: string | null | undefined;
  color: string | null | undefined;
  className?: string;
}) {
  return (
    <span
      className={'inline-block rounded-sm border border-ink-200 align-middle ' + className}
      style={{ width: 24, height: 16, ...patternStyle(pattern, color) }}
    />
  );
}
